import React from "react";

import { useQuoteCountdown } from "@/shared/hooks/useQuoteCountdown";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { quoteFeeView, type NativeQuote } from "../../services/paymentsQuotes.service";
import type { EnhancedCountry } from "../../types/dispersions-api.types";
import { formatDecimalAmount2, getCurrencySymbol } from "../../utils/currencyFormatter";

const CLOCK_SVG = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

export interface QuoteRateBarProps {
  quote: NativeQuote;
  destinationCountry: EnhancedCountry | undefined;
}

/**
 * "Tasa válida por MM:SS" bar shown on steps 2/3. The 1-second countdown tick is INTENTIONALLY confined here:
 * while a quote is active this ~40-line component re-renders every second instead of the whole 700+ line
 * wizard page (the page only listens for the expiry EVENT via `useQuoteExpiry`).
 */
export const QuoteRateBar = React.memo(function QuoteRateBar({
  quote,
  destinationCountry,
}: QuoteRateBarProps) {
  // `activeLanguage` because this bar sits on the same screen as the grouped amount input and the quote
  // breakdown, and all three now write a figure the same way: the reader's separators, always grouped.
  const { t, activeLanguage } = useTranslations("dispersions");
  const { formattedTime, timeRemaining } = useQuoteCountdown(quote.expires_at ?? null);

  const view = quoteFeeView(quote);
  const recvChip = destinationCountry
    ? `${destinationCountry.currencySymbol} ${formatDecimalAmount2(view.beneficiaryReceives, activeLanguage)} ${destinationCountry.currency}`
    : "";
  // Cross-currency also shows the debit, so steps 2/3 never hide which wallet pays.
  const sendChip = view.isCrossCurrency
    ? `${getCurrencySymbol(quote.currency.from)} ${formatDecimalAmount2(quote.amounts.send, activeLanguage)} ${quote.currency.from}`
    : null;

  // Info (blue) → warning (amber) when ≤1 min remains, as in the design.
  const warn = timeRemaining > 0 && timeRemaining <= 60_000;

  return (
    <div
      className={cn(
        "flex items-center gap-[9px] rounded-[10px] px-4 py-2.5 text-sm",
        warn ? "bg-warning-container text-warning-container-foreground" : "bg-muted text-foreground",
      )}
    >
      {CLOCK_SVG}
      <span>
        {t("quote_rate_valid_for")} <b className="tabular-nums">{formattedTime}</b>
      </span>
      {sendChip && (
        <>
          <span className="opacity-50">·</span>
          <span>
            {t("step1_origin_label")} <b className="tabular-nums">{sendChip}</b>
          </span>
        </>
      )}
      {recvChip && (
        <>
          <span className="opacity-50">·</span>
          <span>
            {t("quote_receives_label")} <b className="tabular-nums">{recvChip}</b>
          </span>
        </>
      )}
    </div>
  );
});
